import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, FolderTree, ListChecks, Check } from 'lucide-react';
import { triggerConfetti } from '../lib/confetti';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface MeetingOnboardingProps {
  onClose: () => void;
  onDontShowAgain: () => void;
}

interface StepData {
  icon: typeof FileText;
  titleKey: string;
  descriptionKey: string;
  color: string;
}

const steps: StepData[] = [
  {
    icon: FileText,
    titleKey: 'meetingOnboarding.step1_title',
    descriptionKey: 'meetingOnboarding.step1_description',
    color: '#013EFB'
  },
  {
    icon: FolderTree,
    titleKey: 'meetingOnboarding.step2_title',
    descriptionKey: 'meetingOnboarding.step2_description',
    color: '#2563EB'
  },
  {
    icon: ListChecks,
    titleKey: 'meetingOnboarding.step3_title',
    descriptionKey: 'meetingOnboarding.step3_description',
    color: '#059669'
  },
  {
    icon: Check,
    titleKey: 'meetingOnboarding.step4_title',
    descriptionKey: 'meetingOnboarding.step4_description',
    color: '#7C3AED'
  }
];

export default function MeetingOnboarding({ onClose, onDontShowAgain }: MeetingOnboardingProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      triggerConfetti();
      setTimeout(() => {
        handleClose();
      }, 500);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    if (dontShowAgain) {
      onDontShowAgain();
    } else {
      onClose();
    }
  };

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="max-w-2xl rounded-3xl p-0 gap-0 border-0 overflow-hidden [&>button]:hidden"
      >
        <DialogHeader className="p-6 border-b border-border sr-only">
          <DialogTitle>{t('meetingOnboarding.dialog_title')}</DialogTitle>
          <DialogDescription>{t('meetingOnboarding.dialog_description')}</DialogDescription>
        </DialogHeader>

        {/* Content */}
        <div className="p-12 text-center">
          {/* Icon */}
          <div
            className="w-24 h-24 mx-auto mb-8 rounded-3xl flex items-center justify-center relative overflow-hidden shadow-lg transition-colors duration-500"
            style={{ backgroundColor: currentStepData.color }}
          >
            <div
              className="absolute inset-0 opacity-20"
              style={{
                background: `radial-gradient(circle at 30% 30%, white, transparent)`
              }}
            />
            <Icon size={48} className="text-white relative z-10" strokeWidth={2} />
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold mb-4 text-foreground transition-all duration-300">
            {t(currentStepData.titleKey)}
          </h2>

          {/* Description */}
          <p className="text-lg text-muted-foreground leading-relaxed max-w-lg mx-auto mb-8">
            {t(currentStepData.descriptionKey)}
          </p>

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 mb-10">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={cn(
                  "h-2 rounded-full transition-all duration-300",
                  currentStep === index ? "w-8" : "w-2 bg-muted"
                )}
                style={{
                  backgroundColor: currentStep === index ? currentStepData.color : undefined
                }}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="flex-col px-12 pb-8 gap-6 sm:flex-col sm:space-x-0">
          {/* Buttons */}
          <div className="flex gap-4 w-full">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                className="flex-1 h-12 rounded-xl font-semibold"
              >
                {t('previous')}
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="flex-1 h-12 rounded-xl font-semibold text-white hover:shadow-xl transition-all hover:scale-[1.02]"
              style={{ backgroundColor: currentStepData.color }}
            >
              {isLastStep ? t('meetingOnboarding.start') : t('next')}
            </Button>
          </div>

          {/* Checkbox */}
          <div className="flex items-center justify-center gap-3">
            <Checkbox
              id="meeting-dont-show-again"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
              className="data-[state=checked]:text-white"
              style={{
                borderColor: dontShowAgain ? currentStepData.color : undefined,
                backgroundColor: dontShowAgain ? currentStepData.color : undefined
              }}
            />
            <Label
              htmlFor="meeting-dont-show-again"
              className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
            >
              {t('meetingOnboarding.dont_show_again')}
            </Label>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
